import { useRouter } from "next/router";
import React from "react";
import { useIntl } from "react-intl";

import {
  BookingFormSubmitProps,
  useBookingFormSubmit,
} from "shared/hooks/bookingFormSubmit";
import {
  RYANAIR_CONDITIONS_TIME_LIMIT_ALERT_NAME,
  normalizeBookingFormAlertButtons,
  normalizeBookingFormAlertInfo,
} from "shared/lib/bookingFormAlerts";

import BookingFormInfoModal from "@components/BookingForm/BookingFormInfoModal";
import Img from "@components/FileServer/Img";
import BookingPriceConfirmation from "@components/Flights/BookingPriceConfirmation";
import TermsAndConditions from "@components/Flights/TermsAndConditions";
import FormattedMessage from "@components/UI/FormattedMessage";
import useModalAlert from "@components/UI/useModalAlert";

import { addPaymentInfo } from "@lib/dataLayers";
import {
  errorCodesToRedirect,
  routeChangeFallback,
  scrollToErrorDynamicForm,
} from "@lib/webFunctions";

export const REDIRECT_TIMEOUT_MS = 500;
const RYANAIR_GENERAL_TERMS_URL =
  "https://www.ryanair.com/gb/en/useful-info/help-centre/terms-and-conditions";
const RYANAIR_WEBSITE_TERMS_URL =
  "https://www.ryanair.com/gb/en/lp/legal/terms-of-use";
const RYANAIR_COOKIE_POLICY_URL = "https://www.ryanair.com/gb/en/cookie-policy";
const RYANAIR_PRIVACY_STATEMENT_URL =
  "https://www.ryanair.com/gb/en/lp/privacy-policy";
const RYANAIR_NOTICE_URL =
  "https://www.ryanair.com/gb/en/lp/third-party-notice";

const createRyanairTermsLink = (href: string) => (chunks: React.ReactNode) => (
  <a
    href={href}
    className="link"
    target="_blank"
    rel="noopener noreferrer"
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => {
      event.stopPropagation();

      if (event.key === " ") {
        event.preventDefault();
        event.currentTarget.click();
      }
    }}
  >
    {chunks}
  </a>
);

export default function BookingFormSubmit({
  refs,
  fullPrice,
}: BookingFormSubmitProps) {
  const router = useRouter();
  const intl = useIntl();

  const [ModalAlert, showError] = useModalAlert();

  const onSubmitError = async () => {
    await scrollToErrorDynamicForm();
  };

  const redirectToBooked = async ({
    reservationsIds,
    email,
    currency,
    paymentAdditionalPrice,
  }) => {
    try {
      const fullPriceWithPaymentPrice = `${
        Number(fullPrice) + paymentAdditionalPrice
      }`;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "d4.result.bookSuccess",
        fullPrice: fullPriceWithPaymentPrice,
        currency,
        reservationId: reservationsIds,
        eventCallback: async () => {
          await router.push({
            pathname: "/booked",
            query: { reservationsIds, email },
          });
        },
      });

      setTimeout(async () => {
        await router.push({
          pathname: "/booked",
          query: { reservationsIds, email },
        });
      }, REDIRECT_TIMEOUT_MS);

      // eslint-disable-next-line no-empty
    } catch (e) {}
  };

  const showCustomError = ({
    messageId,
    messageText,
    onClose,
  }: {
    messageId?: string;
    messageText?: string;
    onClose?: () => void;
  }) => {
    showError(
      messageId ? intl.formatMessage({ id: messageId }) : messageText,
      false,
      onClose
    );
  };

  const navigateToPayment = async (paymentUrl) => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    window.location.href = paymentUrl;
  };

  const onCustomErrorClose = (errorCode: string) => {
    if (!errorCodesToRedirect.includes(errorCode)) {
      return;
    }

    routeChangeFallback(() => {
      router.back();
    }, router);
  };

  const {
    onSubmit,
    isConditionsConfirmed,
    isConditionsError,
    toggleIsConditionsConfirmed,
    requiresRyanairConditionsConfirmation,
    isRyanairConditionsConfirmed,
    isRyanairConditionsError,
    toggleIsRyanairConditionsConfirmed,
    unconfirmedReservations,
    summaryPrices,
    alternativeCurrencySummaryPrices,
    currency,
    alternativeCurrency,
    onUnconfirmedReservationSubmit,
    acquisitionSubmitAlert,
    onAcquisitionSubmitAlertAction,
    isFetchingServices,
    isSendingBookingForm,
  } = useBookingFormSubmit({
    refs,
    fullPrice,
    onSubmitError,
    redirectToBooked,
    showCustomError,
    navigateToPayment,
    isHotel: false,
    onCustomErrorClose,
    fireGtmEvents: (bookingData) => addPaymentInfo(bookingData, intl, currency),
    alternativeCurrency: router.query?.currency,
  });

  const handleRyanairConditionsKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    toggleIsRyanairConditionsConfirmed();
  };

  const disableButton =
    isSendingBookingForm === true || isFetchingServices === true;

  const acquisitionAlertButtons = normalizeBookingFormAlertButtons(
    acquisitionSubmitAlert?.Buttons?.Button
  ).map((button) => ({
    ...button,
    Highlight: button?.Highlight === "true",
    label: button?.$t,
    onClick: async () => {
      await onAcquisitionSubmitAlertAction(button?.Action);
    },
  }));

  return (
    <div className="container">
      <div className="result-box result-confirmation">
        <div className="result-confirmation-content">
          <TermsAndConditions
            getRefsForFlightOffer={() => refs}
            error={isConditionsError}
            confirmed={isConditionsConfirmed}
            toggleConditions={toggleIsConditionsConfirmed}
          />

          {requiresRyanairConditionsConfirmation && (
            <div
              className={`pointer result-confirmation-ryanair-checkbox ${
                isRyanairConditionsError
                  ? "result-confirmation-ryanair-checkbox-error"
                  : ""
              }`}
              data-test-id="ryanair-booking-form-confirmation"
              role="checkbox"
              aria-checked={isRyanairConditionsConfirmed}
              tabIndex={0}
              onClick={toggleIsRyanairConditionsConfirmed}
              onKeyDown={handleRyanairConditionsKeyDown}
            >
              <div className="result-confirmation-ryanair-checkbox-icon">
                <Img
                  src={`static/images/ico-checkbox-${
                    isRyanairConditionsConfirmed ? "" : "un"
                  }checked.svg`}
                  className="result-confirmation-ryanair-checkbox-image"
                  alt="checkbox icon"
                />
              </div>
              <span className="result-confirmation-ryanair-checkbox-text">
                <FormattedMessage
                  id="Result.ryanairConditionsConfirmation"
                  values={{
                    generalTermsLink: createRyanairTermsLink(
                      RYANAIR_GENERAL_TERMS_URL
                    ),
                    websiteTermsLink: createRyanairTermsLink(
                      RYANAIR_WEBSITE_TERMS_URL
                    ),
                    cookiePolicyLink: createRyanairTermsLink(
                      RYANAIR_COOKIE_POLICY_URL
                    ),
                    privacyStatementLink: createRyanairTermsLink(
                      RYANAIR_PRIVACY_STATEMENT_URL
                    ),
                    noticeLink: createRyanairTermsLink(RYANAIR_NOTICE_URL),
                  }}
                />
              </span>
            </div>
          )}
        </div>

        <br className="only-mobile" />

        <button
          disabled={disableButton}
          className="button-result-finish button-secondary-color"
          onClick={async () => await onSubmit()}
          type="button"
        >
          <FormattedMessage id="Result.finish" />
        </button>
      </div>

      {unconfirmedReservations !== null && (
        <BookingPriceConfirmation
          show
          originalPrice={
            alternativeCurrencySummaryPrices?.OriginalTotalPrice ||
            summaryPrices?.OriginalTotalPrice
          }
          newPrice={
            alternativeCurrencySummaryPrices?.ActualTotalPrice ||
            summaryPrices?.ActualTotalPrice
          }
          currency={alternativeCurrency || currency}
          onConfirm={onUnconfirmedReservationSubmit}
        />
      )}

      <BookingFormInfoModal
        show={!!acquisitionSubmitAlert}
        onClose={() => onAcquisitionSubmitAlertAction("reopen")}
        title={acquisitionSubmitAlert?.Label?.$t}
        content={
          normalizeBookingFormAlertInfo(
            acquisitionSubmitAlert?.AdditionalInfo?.Info
          )?.[0]?.$t
        }
        customButtons={
          acquisitionAlertButtons.length > 0
            ? acquisitionAlertButtons
            : undefined
        }
        contentId={`booking-submit-modal-content ${acquisitionSubmitAlert?.Name}`}
        modalClassName={
          acquisitionSubmitAlert?.Name ===
          RYANAIR_CONDITIONS_TIME_LIMIT_ALERT_NAME
            ? "booking-submit-ryanair-alert"
            : undefined
        }
      />

      {/* @ts-ignore */}
      <ModalAlert />
    </div>
  );
}
